import { makeAutoObservable, runInAction } from 'mobx';
import { Notification } from 'ksnm-common-ui/lib/Notification';
import { minBy, sortBy } from 'lodash-es';

import { cmApi } from '@src/api';
import type { CmApi, Property } from '@src/api';

import { preloaderStore } from '../preloaderStore';
import { CUSTOM_CHECKBOX, EMPTY_PASSWORD_TYPE_VALUE, GROUP_MEMBERSHIP_SORTING, PropertyTypes } from './constants';
import type { MailKeys } from './constants';

export type AllCheckboxRelatedKyes = MailKeys;

export interface PropertyWithType extends Omit<Property, 'type' | 'value'> {
  type?: PropertyTypes;
  value?: string | boolean;
  isEmpty?: boolean;
}

class SettingsStore {
  properties: PropertyWithType[] = [];

  propertiesAffectedUi: PropertyWithType[] = [];

  tabs: { title: string; key: string }[] = [];

  currentTab: string | undefined;

  isChanged = false;

  api: CmApi;

  constructor({ api }: { api: CmApi }) {
    this.api = api;
    makeAutoObservable(this);
  }

  setCurrentTab = (tab: string) => {
    this.currentTab = tab;
  };

  private getValue = (el: PropertyWithType) => ({
    [PropertyTypes.BOOLEAN]: el.value ? el.value === 'true' : el.defaultValue === 'true',
    [PropertyTypes.PASSWORD]: EMPTY_PASSWORD_TYPE_VALUE,
  });

  getUiAffectedProperties = async () => {
    preloaderStore.preloader();
    const { data } = (await this.api.properties.getUiPropertyValue()) as { data: PropertyWithType[] };
    if (data) {
      runInAction(() => {
        this.propertiesAffectedUi = data.map((el) => ({
          ...el,
          value: el.type ? this.getValue(el)[el.type] ?? el.value : undefined,
        }));
        this.isChanged = false;
      });
    }
    preloaderStore.preloader(false);
  };

  fetch = async () => {
    preloaderStore.preloader();
    const { data } = (await this.api.properties.getProperties()) as { data: PropertyWithType[] };

    if (data) {
      const tabs: { title: string; key: string; sortingNumber: number }[] = [];

      for (const checkBoxKey of Object.keys(CUSTOM_CHECKBOX)) {
        const relatedElements: PropertyWithType[] = CUSTOM_CHECKBOX[checkBoxKey].relatedFieldsKeys.map((key: string) =>
          data.find((el) => el.key?.includes(key)),
        );

        const minSortElement = minBy<PropertyWithType>(relatedElements, 'sortingPriority');
        if (!minSortElement) continue;

        if (minSortElement?.sortingPriority) {
          // Проверка на отличие значения от дефолтного (null) в полях относящихся к чексбоксу, кроме поля с типом password
          const someGroupItemsHasValue = relatedElements
            .filter((el) => el?.type !== PropertyTypes.PASSWORD)
            .some((el) => el?.value !== null);

          data.push({
            key: CUSTOM_CHECKBOX[checkBoxKey].key,
            value: String(someGroupItemsHasValue),
            name: CUSTOM_CHECKBOX[checkBoxKey].label,
            type: PropertyTypes.BOOLEAN,
            title: minSortElement.title,
            relation: minSortElement.relation,
            mustBeFilled: false,
            sortingPriority: minSortElement.sortingPriority - 0.1,
          });
        }
      }

      runInAction(() => {
        this.properties = data.map((el) => {
          if (el.relation && !tabs.find((tab) => tab.key === el.relation)) {
            tabs.push({
              title: el.relation,
              key: el.relation,
              sortingNumber: Object.keys(GROUP_MEMBERSHIP_SORTING).findIndex((tabName) => tabName === el.relation),
            });
          }

          return {
            ...el,
            value: el.type ? this.getValue(el)[el.type] ?? el.value : undefined,
          };
        });
        this.tabs = sortBy(tabs, 'sortingNumber');
        this.currentTab = this.currentTab ?? this.tabs?.[0]?.key;
      });
    }
    preloaderStore.preloader(false);
  };

  update = async (values: { properties: PropertyWithType[] }) => {
    preloaderStore.preloader();
    const formatValues = (el: PropertyWithType) => {
      if (el.type === PropertyTypes.PASSWORD) {
        // '' - если кликнули по полю и ни чего не ввели и если пароль не менялся, то записывается пустая строка
        if (el.isEmpty || el.value === EMPTY_PASSWORD_TYPE_VALUE) return '';
        return el.value ? String(el.value) : '';
      }
      if (el.type === PropertyTypes.BOOLEAN) return typeof el.value === 'boolean' ? String(el.value) : el.value;
      return el.value ? String(el.value) : undefined;
    };

    const formattedValues: Property[] = [];

    values.properties.forEach((el) => {
      // убираем кастомные чекбоксы которые были добавлены
      const needFieldToSend = !Object.keys(CUSTOM_CHECKBOX)
        .map((key) => CUSTOM_CHECKBOX[key].key)
        .includes(`${el.key}`);

      if (needFieldToSend) {
        formattedValues.push({
          ...el,
          value: formatValues(el),
        });
      }
    });

    const { error } = await this.api.properties.updateProperties(formattedValues);

    if (!error) {
      await this.fetch();
      Notification({ message: 'Настройки вступят в действие через минуту', type: 'success' });
      runInAction(() => {
        this.isChanged = true;
      });
    }

    preloaderStore.preloader(false);
  };
}

export const settingsStore = new SettingsStore({ api: cmApi });
